import React from 'react';
import { matchPath } from 'react-router-dom';
import { RiDashboardFill, RiPulseLine, RiServerLine, RiPlugLine, RiShieldCheckLine } from 'react-icons/ri';
import { FaUsers } from 'react-icons/fa';

/**
 * THE admin console route table. One row per surface, and every consumer reads
 * this file rather than restating it.
 *
 * There were about to be four copies of the same five `(path, label)` pairs —
 * the sidebar's links, the sidebar test's fixture, the layout's route→title
 * map and the router's `<Route path>` list. The failure that buys is quiet: a
 * path typo in the sidebar that the router does not serve renders a nav link
 * to an empty `main.main-content`, and no test catches it, because each copy is
 * only ever checked against itself.
 *
 * So: `SideMenu` maps `ADMIN_NAV` for its links, `AdminLayout` resolves its
 * title and its range-picker rule through `matchAdminRoute`, and `App` builds
 * its child `<Route>`s from `ADMIN_ROUTES`. A typo now moves the link and the
 * route together, which is a rename rather than a break.
 *
 * The one deliberate copy that stays is the hand-written literal in
 * `SideMenu.test.tsx`: an oracle must not import the thing it verifies.
 */
export interface AdminRouteEntry {
  /** Absolute path pattern, matched with `matchPath`. May carry params. */
  path: string;
  /** The `<h1>` AdminLayout renders, and the sidebar label where one exists. */
  title: string;
  /**
   * Whether the page header offers the range control. False for the two
   * surfaces whose content is not measured over a window — a user list and one
   * user's record are the same rows whatever range is selected, so offering a
   * picker there would imply a filter that does not exist.
   */
  showRange: boolean;
  /** Sidebar glyph, or null for a surface reached only from within the console. */
  navIcon: React.ReactElement | null;
  /**
   * The dynamic import itself, kept as a thunk rather than only as the
   * `React.lazy` wrapper around it. Tests need to await the module — a suite
   * that renders these routes has to resolve every chunk before any assertion's
   * timeout starts — and the alternative was a hand-written second list of the
   * same six imports, in the one file whose whole argument is against copies.
   * That copy would also have been the kind that rots silently: a missing entry
   * only shows up on a cold cache.
   */
  load: () => Promise<{ default: React.ComponentType }>;
  /**
   * Lazily loaded, and that is structural rather than an optimisation to
   * revisit. `recharts` alone measures +105 kB gzipped and lands in these
   * pages; every non-admin user would otherwise download it to render a
   * dashboard they cannot reach.
   *
   * Derived from `load` below, so the two cannot name different modules.
   */
  Component: React.LazyExoticComponent<React.ComponentType>;
}

const ROUTES: Omit<AdminRouteEntry, 'Component'>[] = [
  // The order the console reads as a story: what is happening, who it is
  // happening to, what the users did, what the machine is doing, what it is
  // wired to. User Detail sits beside Users because that is where it is
  // reached from, and is filtered out of the sidebar below.
  {
    path: '/admin/overview',
    title: 'Overview',
    showRange: true,
    navIcon: <RiDashboardFill />,
    load: () => import('./overview/AdminOverview'),
  },
  {
    path: '/admin/users',
    title: 'Users',
    showRange: false,
    navIcon: <FaUsers />,
    load: () => import('./users/AdminUsers'),
  },
  {
    path: '/admin/users/:id',
    title: 'User Detail',
    showRange: false,
    navIcon: null,
    load: () => import('./users/AdminUserDetail'),
  },
  {
    path: '/admin/activity',
    title: 'Activity',
    showRange: true,
    navIcon: <RiPulseLine />,
    load: () => import('./activity/AdminActivity'),
  },
  {
    path: '/admin/system',
    title: 'System',
    showRange: true,
    navIcon: <RiServerLine />,
    load: () => import('./system/AdminSystem'),
  },
  {
    path: '/admin/integrations',
    title: 'Integrations',
    showRange: true,
    navIcon: <RiPlugLine />,
    load: () => import('./integrations/AdminIntegrations'),
  },
  {
    // The VENDORS behind those integrations -- boards, proxies, contact data,
    // LLMs, Gmail -- as the hourly integration evaluator judged them. No range:
    // its windows are fixed (the last 48h against the five days before).
    path: '/admin/vendors',
    title: 'Vendors',
    showRange: false,
    navIcon: <RiShieldCheckLine />,
    load: () => import('./vendors/AdminVendors'),
  },
];

/**
 * The table every consumer reads. `Component` is derived here so a row cannot
 * end up lazily loading one module while its `load` thunk names another.
 */
export const ADMIN_ROUTES: AdminRouteEntry[] = ROUTES.map((entry) => ({
  ...entry,
  Component: React.lazy(entry.load),
}));

/**
 * Where bare `/admin` lands. The first row of the table rather than a second
 * spelling of the same path: the sidebar's first link and the redirect's target
 * are the same fact, and a literal here could disagree with the table while
 * both looked right on their own.
 */
export const ADMIN_INDEX_PATH: string = ADMIN_ROUTES[0].path;

export interface AdminNavEntry {
  label: string;
  path: string;
  icon: React.ReactElement;
}

/** The rows the sidebar shows, in table order. */
export const ADMIN_NAV: AdminNavEntry[] = ADMIN_ROUTES.filter(
  (entry): entry is AdminRouteEntry & { navIcon: React.ReactElement } => entry.navIcon !== null,
).map(({ path, title, navIcon }) => ({ path, label: title, icon: navIcon }));

/**
 * The path as a child of `<Route path="/admin">` — `/admin/users/:id` becomes
 * `users/:id`. Derived rather than stored so the absolute path above stays the
 * only place a segment is spelled.
 */
export const adminChildPath = (entry: AdminRouteEntry): string =>
  entry.path.replace(/^\/admin\//, '');

/**
 * The console surface a pathname is on, or null when it is not one of them.
 *
 * `matchPath` is exact, so `/admin/users` does not swallow `/admin/users/123`
 * and the two entries can sit in the table in reading order.
 */
export const matchAdminRoute = (pathname: string): AdminRouteEntry | null => {
  // Bare `/admin` resolves to whatever it redirects TO. It is a stop, not a
  // surface, but `<Route index>` commits one frame at this pathname before
  // `<Navigate>` lands, and the layout renders in that frame. Without this the
  // console would open on a generic title with the range control missing and
  // then swap both a tick later — and, worse, the layout's `?? 'Admin'`
  // fallback would be carrying real UI rather than covering a case that cannot
  // happen.
  const resolved = pathname === '/admin' ? ADMIN_INDEX_PATH : pathname;
  return ADMIN_ROUTES.find((entry) => matchPath(entry.path, resolved) !== null) ?? null;
};
